/**
 * fiches.ts — the field definitions, ported from the frozen oracle (P1 US-112, FR-19, FR-20).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * 87 FIELDS, AND EVERY ONE OF THEM IS A DECISION SOMEBODY ALREADY MADE
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * 30 for `lamine`, 33 for `creux`, 24 for `bois`. Every label, unit, symbol, default, step and hint
 * below is the oracle's, character for character — including the deliberately unaccented text
 * ("Profiles lamines", "Verifie", "Elancement"), which must not be corrected: the same strings are
 * printed into the note de calcul, and US-116 byte-compares that against oracle-generated output.
 *
 * `test/fiches.test.ts` re-extracts `FICHES` from the frozen oracle at test time and compares the
 * whole structure against this file. That is what makes the claim above checkable rather than
 * merely asserted — this file was generated from that extraction, and the test is what stops it
 * drifting afterwards.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * OPTION LISTS COME FROM THE CATALOG, NEVER FROM HERE
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * The four catalog-backed dropdowns — profiles, grades, hollow sections, timber classes — plus the
 * three closed enumerations spread from `@ronceray/contract`, which derives them from
 * `@ronceray/catalog`. A hard-coded copy would be a second catalog: the dropdown would keep
 * offering a profile the API has stopped accepting, and the user would get a 422 for choosing
 * something the UI showed them.
 *
 * Defaults are the one thing NOT re-derived, because they are UI decisions rather than catalog
 * data — but `UI_DEFAULTS` in the contract publishes them too, and the test asserts all 87 agree.
 * Two copies that disagree is exactly the failure mode P0's metric M9 exists to catch.
 */

import {
  CLASSE_BOIS_NAMES,
  DUREE_NAMES,
  FABRICATION_NAMES,
  NUANCE_NAMES,
  PROFIL_NAMES,
  SCHEMA_STATIQUE_NAMES,
  SECTION_CREUSE_NAMES,
} from '@ronceray/contract';

/** One input field. The single-letter keys are the oracle's own and are kept for diffability. */
export interface Champ {
  /** Field id. Becomes `data-id`, and `mod + '_' + id` becomes the DOM id. */
  readonly id: string;
  /** Label text. */
  readonly l: string;
  /** Control type. Absent means a number input — the oracle's default branch. */
  readonly t?: 'sel' | 'text';
  /** Options, for a `sel`. */
  readonly o?: readonly (string | number)[];
  /** Symbol rendered in `<em>` after the label, e.g. `gamma,M0`. */
  readonly s?: string;
  /** Unit, rendered in the trailing `<span class="u">`. */
  readonly u?: string;
  /** Default value. */
  readonly d: string | number;
  /** Number input step. The oracle defaults it to 1. */
  readonly step?: number;
  /** Hint rendered as a `div.hint` after the row. */
  readonly h?: string;
}

export interface Groupe {
  readonly titre: string;
  readonly champs: readonly Champ[];
}

export interface Fiche {
  /** Note de calcul title. */
  readonly titre: string;
  /** Reference standard line, printed under the title. */
  readonly norme: string;
  readonly groupes: readonly Groupe[];
}

export type FicheModule = 'lamine' | 'creux' | 'bois';

export const FICHES: Readonly<Record<FicheModule, Fiche>> = {
  lamine: {
    titre: 'Profiles lamines I / H / U',
    norme: 'NF EN 1993-1-1 et Annexe Nationale francaise',
    groupes: [
      {
        titre: 'Element et materiau',
        champs: [
          { id: 'repere', l: "Repere de l'element", t: 'text', d: 'Poutre' },
          { id: 'profil', l: 'Profile', t: 'sel', o: [...PROFIL_NAMES], d: 'IPN 220' },
          { id: 'nuance', l: "Nuance d'acier", t: 'sel', o: [...NUANCE_NAMES], d: 'S275' },
          { id: 'gM0', l: 'Coefficient partiel - section', s: 'gamma,M0', d: 1, step: 0.05 },
          { id: 'gM1', l: 'Coefficient partiel - instabilites', s: 'gamma,M1', d: 1, step: 0.05 },
        ],
      },
      {
        titre: 'Longueurs',
        champs: [
          { id: 'L', l: "Portee de l'element", s: 'L', u: 'mm', d: 4500 },
          { id: 'Lcry', l: 'Longueur de flambement axe fort', s: 'Lcr,y', u: 'mm', d: 4500 },
          {
            id: 'Lcrz',
            l: 'Longueur de flambement axe faible',
            s: 'Lcr,z',
            u: 'mm',
            d: 4500,
            h: 'Reduire si maintiens lateraux',
          },
          {
            id: 'LLT',
            l: 'Longueur de deversement',
            s: 'L,LT',
            u: 'mm',
            d: 4500,
            h: 'Entraxe des maintiens de la semelle comprimee',
          },
        ],
      },
      {
        titre: 'Coefficients de moment',
        champs: [
          {
            id: 'C1',
            l: 'Facteur de moment critique',
            s: 'C1',
            d: 1.13,
            step: 0.01,
            h: '1,00 moment uniforme (securitaire) - 1,13 charge repartie',
          },
          {
            id: 'kc',
            l: 'Facteur de correction du diagramme',
            s: 'kc',
            d: 1,
            step: 0.01,
            h: 'Tableau 6.6 - 1,00 est securitaire',
          },
          { id: 'Cmy', l: 'Moment uniforme equivalent y', s: 'Cmy', d: 0.9, step: 0.05 },
          { id: 'Cmz', l: 'Moment uniforme equivalent z', s: 'Cmz', d: 0.9, step: 0.05 },
          { id: 'CmLT', l: 'Moment uniforme equivalent LT', s: 'CmLT', d: 0.9, step: 0.05 },
        ],
      },
      {
        titre: 'Sollicitations ELU',
        champs: [
          {
            id: 'NEd',
            l: 'Effort normal',
            s: 'N,Ed',
            u: 'kN',
            d: 0,
            step: 0.1,
            h: 'POSITIF = compression / NEGATIF = traction',
          },
          { id: 'MyEd', l: 'Moment axe fort', s: 'My,Ed', u: 'kN.m', d: 36.2, step: 0.1 },
          { id: 'MzEd', l: 'Moment axe faible', s: 'Mz,Ed', u: 'kN.m', d: 0, step: 0.1 },
          { id: 'VzEd', l: 'Effort tranchant', s: 'Vz,Ed', u: 'kN', d: 32.2, step: 0.1 },
        ],
      },
      {
        titre: 'Etat limite de service',
        champs: [
          {
            id: 'modeFleche',
            l: 'Mode de calcul',
            t: 'sel',
            o: ['Calculee', 'Saisie manuelle'],
            d: 'Calculee',
          },
          {
            id: 'schema',
            l: 'Schema statique',
            t: 'sel',
            o: [...SCHEMA_STATIQUE_NAMES],
            d: 'Isostatique (appuis simples)',
          },
          { id: 'qGy', l: 'Charge repartie permanente', s: 'qG,y', u: 'kN/m', d: 5.63, step: 0.01 },
          { id: 'qQy', l: 'Charge repartie variable', s: 'qQ,y', u: 'kN/m', d: 4.5, step: 0.01 },
          { id: 'PGy', l: 'Charge ponctuelle permanente', s: 'PG,y', u: 'kN', d: 0, step: 0.1 },
          { id: 'PQy', l: 'Charge ponctuelle variable', s: 'PQ,y', u: 'kN', d: 0, step: 0.1 },
          {
            id: 'qGz',
            l: 'Charge repartie permanente - axe faible',
            s: 'qG,z',
            u: 'kN/m',
            d: 0,
            step: 0.01,
          },
          {
            id: 'qQz',
            l: 'Charge repartie variable - axe faible',
            s: 'qQ,z',
            u: 'kN/m',
            d: 0,
            step: 0.01,
          },
          { id: 'wGman', l: 'Fleche sous G (modele)', s: 'w,G', u: 'mm', d: 0, step: 0.1 },
          { id: 'wQman', l: 'Fleche sous Q (modele)', s: 'w,Q', u: 'mm', d: 0, step: 0.1 },
          { id: 'n1', l: 'Critere fleche totale L/n', s: 'n1', d: 250 },
          { id: 'n2', l: 'Critere fleche sous Q  L/n', s: 'n2', d: 350 },
        ],
      },
    ],
  },
  creux: {
    titre: 'Profils creux SHS / RHS',
    norme: 'NF EN 1993-1-1, EN 10210 / EN 10219',
    groupes: [
      {
        titre: 'Element et materiau',
        champs: [
          { id: 'repere', l: "Repere de l'element", t: 'text', d: 'Poteau' },
          {
            id: 'section',
            l: 'Section',
            t: 'sel',
            o: [...SECTION_CREUSE_NAMES],
            d: 'SHS 100x100x4',
          },
          {
            id: 'hp',
            l: 'h personnalise',
            s: 'h',
            u: 'mm',
            d: 100,
            h: 'Utilise uniquement si la section est sur Personnalise',
          },
          { id: 'bp', l: 'b personnalise', s: 'b', u: 'mm', d: 100 },
          { id: 'tp', l: 't personnalise', s: 't', u: 'mm', d: 4 },
          {
            id: 'fabrication',
            l: 'Mode de fabrication',
            t: 'sel',
            o: [...FABRICATION_NAMES],
            d: 'Fini a chaud (EN 10210)',
          },
          { id: 'nuance', l: "Nuance d'acier", t: 'sel', o: [...NUANCE_NAMES], d: 'S235' },
          { id: 'gM0', l: 'Coefficient partiel - section', s: 'gamma,M0', d: 1, step: 0.05 },
          { id: 'gM1', l: 'Coefficient partiel - instabilites', s: 'gamma,M1', d: 1, step: 0.05 },
        ],
      },
      {
        titre: 'Longueurs',
        champs: [
          { id: 'L', l: 'Portee ou hauteur', s: 'L', u: 'mm', d: 3000 },
          { id: 'Lcry', l: 'Longueur de flambement axe fort', s: 'Lcr,y', u: 'mm', d: 3000 },
          { id: 'Lcrz', l: 'Longueur de flambement axe faible', s: 'Lcr,z', u: 'mm', d: 3000 },
          { id: 'LLT', l: 'Longueur de deversement', s: 'L,LT', u: 'mm', d: 3000 },
        ],
      },
      {
        titre: 'Coefficients de moment',
        champs: [
          { id: 'C1', l: 'Facteur de moment critique', s: 'C1', d: 1, step: 0.01 },
          { id: 'Cmy', l: 'Moment uniforme equivalent y', s: 'Cmy', d: 0.9, step: 0.05 },
          { id: 'Cmz', l: 'Moment uniforme equivalent z', s: 'Cmz', d: 0.9, step: 0.05 },
        ],
      },
      {
        titre: 'Sollicitations ELU',
        champs: [
          {
            id: 'NEd',
            l: 'Effort normal',
            s: 'N,Ed',
            u: 'kN',
            d: 32,
            step: 0.1,
            h: 'POSITIF = compression / NEGATIF = traction',
          },
          { id: 'MyEd', l: 'Moment axe fort', s: 'My,Ed', u: 'kN.m', d: 0, step: 0.1 },
          { id: 'MzEd', l: 'Moment axe faible', s: 'Mz,Ed', u: 'kN.m', d: 0, step: 0.1 },
          { id: 'VzEd', l: 'Effort tranchant parallele a h', s: 'Vz,Ed', u: 'kN', d: 0, step: 0.1 },
          {
            id: 'TEd',
            l: 'Moment de torsion',
            s: 'T,Ed',
            u: 'kN.m',
            d: 0,
            step: 0.1,
            h: "Torsion de Saint-Venant. Laisser 0 en l'absence de torsion.",
          },
        ],
      },
      {
        titre: 'Etat limite de service',
        champs: [
          {
            id: 'modeFleche',
            l: 'Mode de calcul',
            t: 'sel',
            o: ['Calculee', 'Saisie manuelle'],
            d: 'Calculee',
          },
          {
            id: 'schema',
            l: 'Schema statique',
            t: 'sel',
            o: [...SCHEMA_STATIQUE_NAMES],
            d: 'Isostatique (appuis simples)',
          },
          { id: 'qGy', l: 'Charge repartie permanente', s: 'qG,y', u: 'kN/m', d: 0, step: 0.01 },
          { id: 'qQy', l: 'Charge repartie variable', s: 'qQ,y', u: 'kN/m', d: 0, step: 0.01 },
          { id: 'PGy', l: 'Charge ponctuelle permanente', s: 'PG,y', u: 'kN', d: 0, step: 0.1 },
          { id: 'PQy', l: 'Charge ponctuelle variable', s: 'PQ,y', u: 'kN', d: 0, step: 0.1 },
          {
            id: 'qGz',
            l: 'Charge repartie permanente - axe faible',
            s: 'qG,z',
            u: 'kN/m',
            d: 0,
            step: 0.01,
          },
          {
            id: 'qQz',
            l: 'Charge repartie variable - axe faible',
            s: 'qQ,z',
            u: 'kN/m',
            d: 0,
            step: 0.01,
          },
          { id: 'wGman', l: 'Fleche sous G (modele)', s: 'w,G', u: 'mm', d: 0, step: 0.1 },
          { id: 'wQman', l: 'Fleche sous Q (modele)', s: 'w,Q', u: 'mm', d: 0, step: 0.1 },
          { id: 'n1', l: 'Critere fleche totale L/n', s: 'n1', d: 250 },
          { id: 'n2', l: 'Critere fleche sous Q  L/n', s: 'n2', d: 350 },
        ],
      },
    ],
  },
  bois: {
    titre: 'Bois - flexion composee',
    norme: 'NF EN 1995-1-1 et Annexe Nationale francaise',
    groupes: [
      {
        titre: 'Element et materiau',
        champs: [
          { id: 'repere', l: "Repere de l'element", t: 'text', d: 'Solive' },
          {
            id: 'classe',
            l: 'Classe de resistance',
            t: 'sel',
            o: [...CLASSE_BOIS_NAMES],
            d: 'C24',
          },
          {
            id: 'classeService',
            l: 'Classe de service',
            t: 'sel',
            o: [1, 2, 3],
            d: 2,
            h: '1 interieur chauffe / 2 abrite ventile / 3 exterieur',
          },
          {
            id: 'duree',
            l: 'Classe de duree de charge',
            t: 'sel',
            o: [...DUREE_NAMES],
            d: 'Moyen terme',
          },
          {
            id: 'situation',
            l: 'Situation de calcul',
            t: 'sel',
            o: ['Normale', 'Accidentelle (incendie)'],
            d: 'Normale',
          },
        ],
      },
      {
        titre: 'Geometrie',
        champs: [
          { id: 'b', l: 'Largeur de la section', s: 'b', u: 'mm', d: 100 },
          { id: 'h', l: 'Hauteur de la section', s: 'h', u: 'mm', d: 150 },
          { id: 'L', l: "Portee de l'element", s: 'L', u: 'mm', d: 4500 },
          { id: 'Lcry', l: 'Longueur de flambement plan y-y', s: 'Lcr,y', u: 'mm', d: 4500 },
          {
            id: 'Lcrz',
            l: 'Longueur de flambement plan z-z',
            s: 'Lcr,z',
            u: 'mm',
            d: 500,
            h: 'Reduite si maintien lateral',
          },
          {
            id: 'lef',
            l: 'Longueur efficace de deversement',
            s: 'lef',
            u: 'mm',
            d: 500,
            h: 'Tableau 6.1 - entraxe des maintiens de la fibre comprimee',
          },
        ],
      },
      {
        titre: 'Sollicitations ELU',
        champs: [
          {
            id: 'NEd',
            l: 'Effort normal',
            s: 'N,Ed',
            u: 'kN',
            d: 0.6,
            step: 0.1,
            h: 'POSITIF = compression / NEGATIF = traction',
          },
          { id: 'MyEd', l: 'Moment axe fort', s: 'My,Ed', u: 'kN.m', d: 3.2, step: 0.1 },
          { id: 'MzEd', l: 'Moment axe faible', s: 'Mz,Ed', u: 'kN.m', d: 0, step: 0.1 },
          { id: 'VEd', l: 'Effort tranchant', s: 'V,Ed', u: 'kN', d: 2.9, step: 0.1 },
          { id: 'Fc90Ed', l: "Reaction d'appui", s: 'Fc,90,Ed', u: 'kN', d: 2.9, step: 0.1 },
          { id: 'lappui', l: "Longueur d'appui", s: 'l,appui', u: 'mm', d: 80 },
          {
            id: 'kc90',
            l: 'Coefficient kc,90',
            s: 'kc,90',
            d: 1.5,
            step: 0.05,
            h: '1,00 par defaut - 1,25 a 1,50 si appui continu',
          },
          { id: 'kcr', l: 'Coefficient de fissuration', s: 'kcr', d: 0.67, step: 0.01 },
          { id: 'km', l: 'Coefficient de flexion deviee', s: 'km', d: 0.7, step: 0.05 },
        ],
      },
      {
        titre: 'Etat limite de service',
        champs: [
          {
            id: 'winstG',
            l: 'Fleche instantanee sous G',
            s: 'w,inst,G',
            u: 'mm',
            d: 4.3,
            step: 0.01,
            h: 'Issue du modele',
          },
          {
            id: 'winstQ',
            l: 'Fleche instantanee sous Q',
            s: 'w,inst,Q',
            u: 'mm',
            d: 3.45,
            step: 0.01,
          },
          { id: 'psi2', l: "Coefficient psi2 de l'action Q", s: 'psi2', d: 0, step: 0.05 },
          { id: 'nlim', l: 'Critere de limitation L/n', s: 'n', d: 300 },
        ],
      },
    ],
  },
};

/** Every field of a module, flattened in render order. */
export function champs(mod: FicheModule): readonly Champ[] {
  return FICHES[mod].groupes.flatMap((groupe) => groupe.champs);
}

/** Field counts, asserted by the test: 30 / 33 / 24, and 87 in total. */
export const CHAMP_COUNTS: Readonly<Record<FicheModule, number>> = {
  lamine: champs('lamine').length,
  creux: champs('creux').length,
  bois: champs('bois').length,
};
